import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type AuthReport } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";

export function ObservabilityAuthPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [data, setData] = useState<AuthReport | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const { data: rep } = await api.authReport(id);
        setData(rep);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading auth observability…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Auth"
        description="Sign-in, sign-up, token refresh metrics and security events for this project."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {!data ? (
        <p className="text-sm text-text-muted">No auth metrics.</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Sign-ins OK" value={data.sign_ins_success.toLocaleString()} />
            <StatTile label="Sign-ins failed" value={data.sign_ins_failed.toLocaleString()} />
            <StatTile label="Sign-ups" value={data.sign_ups.toLocaleString()} />
            <StatTile
              label="Refresh fail rate"
              value={
                data.token_refresh_ok + data.token_refresh_fail > 0
                  ? `${(
                      (data.token_refresh_fail /
                        (data.token_refresh_ok + data.token_refresh_fail)) *
                      100
                    ).toFixed(1)}%`
                  : "—"
              }
              hint={`${data.token_refresh_ok} ok / ${data.token_refresh_fail} fail`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatTile label="Sign-outs" value={data.sign_outs.toLocaleString()} />
            <StatTile label="Auth requests" value={data.total_auth_requests.toLocaleString()} />
            <StatTile label="Error rate" value={`${data.error_rate.toFixed(2)}%`} />
          </div>

          {data.by_route.length > 0 && (
            <div className="card p-4">
              <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">By route</h3>
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th>Method</th>
                    <th className="text-right">Calls</th>
                    <th className="text-right">Errors</th>
                    <th className="text-right">Avg ms</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_route.map((r, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">{r.route_template}</td>
                      <td className="mono-chip">{r.method}</td>
                      <td className="text-right tabular-nums">{r.calls}</td>
                      <td className="text-right tabular-nums">{r.errors}</td>
                      <td className="text-right tabular-nums">{r.avg_duration_ms.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.security_events.length > 0 && (
            <div className="card p-4">
              <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Security events</h3>
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Kind</th>
                    <th>Route</th>
                    <th className="text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.security_events.map((e) => (
                    <tr key={e.event_id}>
                      <td className="text-xs text-text-muted">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td>
                        <span className="mono-chip">{e.kind}</span>
                      </td>
                      <td className="font-mono text-xs">{e.route_template}</td>
                      <td className="text-right tabular-nums text-error">{e.status_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
